import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../shared/hooks/useAuth";

import "../../shared/styles/UtillitiesComponent.css";
import "../../shared/styles/pages/Auth.css";

import { BsTelephone } from "react-icons/bs";

import chicchic_login_logo from "../../assets/chicchic_login_logo.png";
import chicchic_logo_darkmode from "../../assets/chicchic_logo_darkmode.png";

function normalizeThaiPhone(phone) {
  const digits = phone.replace(/\D/g, "");

  if (digits.startsWith("0")) return "+66" + digits.slice(1);
  if (digits.startsWith("66")) return "+" + digits;

  return phone;
}

export default function Login() {
  const { loginGoogle, sendOtp, verifyOtp } = useAuth();

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  // const redirectTo = location.state?.from?.pathname || "/";
  const from = location.state?.from;
  const redirectTo = from
    ? `${from.pathname || ""}${from.search || ""}${from.hash || ""}`
    : "/";

  function humanizeAuthError(e) {
    const code = e?.code || "";

    if (code === "auth/invalid-phone-number") {
      return "รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง";
    }
    if (code === "auth/invalid-verification-code") {
      return "รหัส OTP ไม่ถูกต้อง";
    }
    if (code === "auth/code-expired") {
      return "รหัส OTP หมดอายุ กรุณาขอรหัสใหม่";
    }
    if (code === "auth/too-many-requests") {
      return "มีการลองหลายครั้งเกินไป กรุณาลองใหม่ภายหลัง";
    }
    if (code === "auth/network-request-failed") {
      return "เครือข่ายมีปัญหา กรุณาตรวจสอบอินเทอร์เน็ต";
    }
    if (code === "auth/popup-closed-by-user") {
      return "คุณปิดหน้าต่างล็อกอินก่อนดำเนินการเสร็จ";
    }
    if (code === "auth/cancelled-popup-request") {
      return "มีคำขอเปิดหน้าต่างล็อกอินหลายครั้ง";
    }
    if (code === "auth/popup-blocked") {
      return "เบราว์เซอร์บล็อกหน้าต่างล็อกอิน กรุณาอนุญาตป๊อปอัพ";
    }
    if (code === "auth/invalid-app-credential") {
      return "ไม่สามารถยืนยันความปลอดภัยของระบบได้ กรุณาลองใหม่อีกครั้ง";
    }

    return e?.message || "เกิดข้อผิดพลาด ไม่สามารถลงชื่อเข้าใช้ได้";
  }

  const doLoginGoogle = async () => {
    try {
      setSubmitting(true);
      await loginGoogle();
      navigate(redirectTo, { replace: true });
    } catch (e) {
      alert(humanizeAuthError(e));
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!cooldown) return;
    const timer = setInterval(() => {
      setCooldown((c) => Math.max(0, c - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  const doSendOtp = async () => {
    if (cooldown > 0) return;

    try {
      setSubmitting(true);

      const formattedPhone = normalizeThaiPhone(phone);
      const conf = await sendOtp(formattedPhone, { invisible: true });

      setConfirmation(conf);
      setCooldown(60);
    } catch (e) {
      alert(humanizeAuthError(e));
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const doVerifyOtp = async () => {
    try {
      setSubmitting(true);
      await verifyOtp(confirmation, otp);

      setOtp("");
      setConfirmation(null);

      navigate(redirectTo, { replace: true });
    } catch (e) {
      alert(humanizeAuthError(e));
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const resetPhoneFlow = () => {
    setConfirmation(null);
    setOtp("");
  };

  return (
    <section className="auth-shell auth-shell-bubbles-login">
      <div className="auth-card auth-card-centered">
        <div className="auth-logo-wrapper">
          <img
            src={chicchic_logo_darkmode}
            alt="ChicChic Logo"
            className="auth-logo-img"
          />
        </div>

        <h3 className="mb-2 text-center">ลงชื่อเข้าใช้</h3>

        <p className="text-center text-secondary small mb-3">
          ลงชื่อเข้าใช้หรือสมัครใหม่ได้ด้วย Google หรือเบอร์โทรศัพท์
        </p>


        <div className="card app-card auth-theme-card mb-3">
          <div className="card-body">
            <button
              className="google-btn w-100"
              onClick={doLoginGoogle}
              disabled={submitting}
            >
              <span className="google-btn-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                  />
                  <path
                    fill="#4285F4"
                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                  />
                  <path
                    fill="#34A853"
                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                  />
                </svg>
              </span>
              <span className="google-btn-text">ลงชื่อเข้าใช้ด้วย Google</span>
            </button>

            <div className="text-secondary small mt-2 text-center">
              ใช้ได้ทั้งเข้าสู่ระบบและสมัครใหม่
            </div>
          </div>
        </div>

        <div className="card app-card auth-theme-card mb-3">
          <div className="card-body">
            <h6 className="mb-3 text-center d-flex align-items-center justify-content-center gap-2">
              <BsTelephone />
              ลงชื่อเข้าใช้ด้วยโทรศัพท์
            </h6>

            <input
              className="form-control mb-2"
              type="tel"
              placeholder="เช่น 0801234567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={submitting}
            />

            {!confirmation ? (
              <>
                <button
                  className="btn btn-outline-primary w-100"
                  onClick={doSendOtp}
                  disabled={submitting || !phone || cooldown > 0}
                >
                  {cooldown > 0 ? `ส่งได้อีกใน ${cooldown}s` : "ส่งรหัส OTP"}
                </button>

                <div className="text-muted small mt-2 text-center">
                  ระบบจะส่งรหัส OTP ทาง SMS เพื่อเข้าสู่ระบบหรือสมัครใหม่
                </div>
              </>
            ) : (
              <>
                <input
                  className="form-control mb-2"
                  placeholder="กรอกรหัส OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  disabled={submitting}
                />

                <button
                  className="btn btn-primary w-100"
                  onClick={doVerifyOtp}
                  disabled={submitting || !otp}
                >
                  ยืนยันรหัส OTP
                </button>

                <div className="text-muted small mt-2 text-center">
                  กรอกรหัส OTP ที่ได้รับทาง SMS จาก CloudOTP
                </div>

                <div className="text-center mt-2">
                  <button
                    type="button"
                    className="btn btn-link p-0 small no-underline-link"
                    onClick={resetPhoneFlow}
                    disabled={submitting}
                  >
                    เปลี่ยนเบอร์โทรศัพท์
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="text-center text-secondary small mb-3">
          หากเคยใช้งานแล้ว กรุณาใช้วิธีเดิมที่เคยใช้ในการลงชื่อเข้าใช้
        </div>
        <div className="text-center text-secondary small">
          เมื่อดำเนินการต่อ ถือว่าคุณยอมรับเงื่อนไขการใช้งานของระบบ
        </div>
      </div>
    </section>
  );
}

